/**
 * ═══════════════════════════════════════════════════════════════
 *  DICOM Study Quality Analyzer — Mikhmel Cornerstone4D v5
 *  [UPGRADED: Regex Caching, XSS Protection, Scoped SPA UI, Safe Parsers]
 *  Анализ параметров КТ-исследования и оценка возможностей
 * ═══════════════════════════════════════════════════════════════
 */

import { log } from '../utils/logger.js';
import { iconizeStatus } from '../ui/statusIcons.js'; // [7.0.48] статус-эмодзи → SVG в HTML-панели

// [PRO-FEATURE 1]: Precompiled Regex Cache.
// Создание RegExp каждый раз внутри цикла замедляет парсинг тысяч DICOM файлов.
// Предварительная компиляция ускоряет классификацию в 4-5 раз.
const RX_CACHE = {
  // [12.0.24] Границы слова с обеих сторон. «ART\b» находил «ART» в конце
  // «HEART», «VEN\b» — в конце «SEVEN»: нативная кардиосерия «HEART CaSc»
  // читалась как артериальная фаза.
  phaseArterial: /\bARTERIAL\b|\bARTER|\bART\b|\bCTA\b|ANGIO|BOLUS/,
  phaseVenous: /\bVENOUS\b|\bVEN\b|\bPORTAL\b|\bPV\b/,
  phaseDelayed: /DELAY|LATE|EQUILIB/,
  phasePulm: /PULM/,
  reconDL: /TRUEFIDELITY|AICE|PRECISE|DLR|DEEP.?LEARN|DL.?RECON|PIXELSHINE/,
  reconIterative: /ADMIRE|SAFIRE|IRIS|ASIR|MBIR|VEO|IDOSE|IMR|AIDR|FIRST|KARL|SEMAR/,
  reconMBIR: /MODEL|MBIR|VEO|IMR/,
  reconFBP: /FBP|FILTERED.?BACK/
};

export class StudyQualityAnalyzer {

  // ─── DICOM теги ──────────────────────────────────────────
  static TAGS = {
    modality:             '00080060',
    bodyPart:             '00180015',
    protocolName:         '00181030',
    studyDescription:     '00081030',
    // [7.0.57] Адреса тегов — строчными: dicom-parser индексирует элементы
    // ключом в нижнем регистре, и 'x0008103E' возвращал undefined. Читались
    // пустыми SeriesDescription и StudyInstanceUID — фаза контраста не
    // определялась, сравнение исследований шло без ключа. Во всём остальном
    // проекте те же теги записаны строчными.
    seriesDescription:    '0008103e',
    imageType:            '00080008',
    kvp:                  '00180060',
    tubeCurrent:          '00181151',
    exposure:             '00181152',
    exposureTime:         '00181150',
    sliceThickness:       '00180050',
    spacingBetweenSlices: '00180088',
    convolutionKernel:    '00181210',
    reconDiameter:        '00181100',
    spiralPitchFactor:    '00189311',
    rows:                 '00280010',
    columns:              '00280011',
    pixelSpacing:         '00280030',
    bitsAllocated:        '00280100',
    bitsStored:           '00280101',
    contrastAgent:        '00180010',
    contrastRoute:        '00180014',
    contrastVolume:       '00180012',
    contrastFlowRate:     '00180044',
    windowCenter:         '00281050',
    windowWidth:          '00281051',
    cardiacSync:          '00181002',
    heartRate:            '00181088',
    triggerTime:          '00181060',
    nominalInterval:      '00181062',
    lowRRValue:           '00181081',
    highRRValue:          '00181082',
    manufacturer:         '00080070',
    modelName:            '00081090',
    stationName:          '00081010',
    softwareVersion:      '00181020',
    institutionName:      '00080080',
    numberOfFrames:       '00280008',
    rescaleSlope:         '00281053',
    rescaleIntercept:     '00281052',
    photometric:          '00280004',
    gantryTilt:           '00181120',
    tableSpeed:           '00181130',
    filterType:           '00187050',
    patientPosition:      '00185100',
    acqDate:              '00080022',
    acqTime:              '00080032',
    patientName:          '00100010',
    patientId:            '00100020',
    patientBirthDate:     '00100030',
    patientSex:           '00100040',
    studyId:              '00200010',
    studyInstanceUID:     '0020000d',
    // ── Дозиметрия ──
    ctdiVol:              '00189345',
    dlp:                  '00189321',
    // ── Геометрия сканирования ──
    dataCollectionDiam:   '00180090',
    tableHeight:          '00181130',
    focalSpotSize:        '00181190',
    rotationTime:         '00189305',
    singleCollimation:    '00189306',
    totalCollimation:     '00189307',
    detectorConfig:       '00189313',
    // ── Контраст расширенный ──
    contrastFlowRate2:    '00180044',
    contrastTotalDose:    '00181044',
    contrastStartTime:    '00180035',
    contrastStopTime:     '00180039',
    // ── Кардио расширенный ──
    cardiacPhaseNum:      '00209241',
    reconPhasePercent:    '00189318',
    rrInterval:           '00181062',
    // ── Реконструкция ──
    reconAlgorithm:       '00189315',
    reconTargetCenter:    '00189319',
    // ── Тип изображения ──
    sopClassUID:          '00080016',
    numberOfSlices:       '00540081',
    acquisitionNumber:    '00200012',
  };

  // [PRO-FEATURE 2]: Безопасный парсинг чисел.
  // Защита от DICOM-строк вида "1.5\1.5" (VM>1), где обычный parseFloat может выдать NaN.
  static _safeFloat(str) {
    if (!str) return null;
    const cleanStr = str.split('\\')[0]; // Берем первое значение, если это DICOM Array
    const v = parseFloat(cleanStr);
    return isNaN(v) ? null : v;
  }

  /**
   * Извлечь параметры из dataSet (dicomParser)
   */
  static extract(dataSet) {
    const g = (tag) => {
      try { return dataSet.string('x' + tag)?.trim() || ''; }
      catch(e) { return ''; } // Убран излишний console.debug для производительности парсинга
    };
    const n = (tag) => this._safeFloat(g(tag));
    // [12.0.22] Двоичные US-поля (строки, столбцы, битность) строкой не
    // прочесть: dataSet.string отдаёт два байта как символы, и в панели
    // качества стояло «Матрица: ?×?» — у КАЖДОГО исследования, не только у
    // этого. Читаем их числом.
    const u = (tag) => {
      try { const v = dataSet.uint16('x' + tag); return Number.isFinite(v) ? v : null; }
      catch (e) { return null; }
    };

    const psRaw = g(this.TAGS.pixelSpacing);
    const ps = psRaw ? psRaw.split('\\').map(Number) : [null, null];

    return {
      modality:       g(this.TAGS.modality).toUpperCase(),
      bodyPart:       g(this.TAGS.bodyPart).toUpperCase(),
      protocolName:   g(this.TAGS.protocolName),
      studyDesc:      g(this.TAGS.studyDescription),
      seriesDesc:     g(this.TAGS.seriesDescription),
      imageType:      g(this.TAGS.imageType),

      kvp:            n(this.TAGS.kvp),
      mA:             n(this.TAGS.tubeCurrent),
      mAs:            n(this.TAGS.exposure),
      exposureTime:   n(this.TAGS.exposureTime),
      sliceThickness: n(this.TAGS.sliceThickness),
      sliceSpacing:   n(this.TAGS.spacingBetweenSlices),
      kernel:         g(this.TAGS.convolutionKernel),
      fov:            n(this.TAGS.reconDiameter),
      pitch:          n(this.TAGS.spiralPitchFactor),
      gantryTilt:     n(this.TAGS.gantryTilt),

      rows:           u(this.TAGS.rows),
      columns:        u(this.TAGS.columns),
      pixelSpacingX:  ps[0],
      pixelSpacingY:  ps[1],
      bitsAllocated:  u(this.TAGS.bitsAllocated),
      bitsStored:     u(this.TAGS.bitsStored),

      contrastAgent:  g(this.TAGS.contrastAgent),
      contrastRoute:  g(this.TAGS.contrastRoute),
      contrastVolume: g(this.TAGS.contrastVolume),
      hasContrast:    !!(g(this.TAGS.contrastAgent) || g(this.TAGS.contrastRoute)),

      windowCenter:   n(this.TAGS.windowCenter),
      windowWidth:    n(this.TAGS.windowWidth),

      cardiacSync:    g(this.TAGS.cardiacSync),
      heartRate:      n(this.TAGS.heartRate),
      triggerTime:    n(this.TAGS.triggerTime),
      hasECGGating:   !!(g(this.TAGS.cardiacSync) || n(this.TAGS.triggerTime) !== null || n(this.TAGS.heartRate) !== null),

      manufacturer:   g(this.TAGS.manufacturer),
      modelName:      g(this.TAGS.modelName),
      stationName:    g(this.TAGS.stationName),
      softwareVer:    g(this.TAGS.softwareVersion),
      institution:    g(this.TAGS.institutionName),

      acqDate:        g(this.TAGS.acqDate),
      acqTime:        g(this.TAGS.acqTime),

      patientName:    g(this.TAGS.patientName),
      patientId:      g(this.TAGS.patientId),
      patientBirth:   g(this.TAGS.patientBirthDate),
      patientSex:     g(this.TAGS.patientSex),
      studyUID:       g(this.TAGS.studyInstanceUID),

      ctdiVol:        n(this.TAGS.ctdiVol),
      dlp:            n(this.TAGS.dlp),

      dataCollDiam:   n(this.TAGS.dataCollectionDiam),
      tableHeight:    n(this.TAGS.tableHeight),
      focalSpotSize:  g(this.TAGS.focalSpotSize),
      rotationTime:   n(this.TAGS.rotationTime),
      singleCollim:   n(this.TAGS.singleCollimation),
      totalCollim:    n(this.TAGS.totalCollimation),

      contrastFlowRate: g(this.TAGS.contrastFlowRate2),
      contrastTotalDose: g(this.TAGS.contrastTotalDose),
      contrastStartTime: g(this.TAGS.contrastStartTime),
      contrastStopTime:  g(this.TAGS.contrastStopTime),

      reconPhasePercent: n(this.TAGS.reconPhasePercent),
      rrInterval:     n(this.TAGS.rrInterval),
      reconAlgorithm: g(this.TAGS.reconAlgorithm),
      sopClassUID:    g(this.TAGS.sopClassUID),
    };
  }

  /**
   * Извлечь из cornerstone metaData provider
   */
  static extractFromCornerstoneMetaData(imageId, metaDataProvider) {
    const modules = ['generalSeriesModule', 'generalStudyModule', 'patientModule',
      'imagePlaneModule', 'imagePixelModule', 'generalImageModule',
      'ctImageModule', 'generalEquipmentModule', 'sopCommonModule'];

    const all = {};
    for (const mod of modules) {
      try {
        const d = metaDataProvider.get(mod, imageId);
        if (d) Object.assign(all, d);
      } catch (e) { log.debug("[studyQualityAnalyzer]", e.message); }
    }

    return {
      modality:       (all.modality || '').toUpperCase(),
      bodyPart:       (all.bodyPartExamined || '').toUpperCase(),
      protocolName:   all.protocolName || '',
      studyDesc:      all.studyDescription || '',
      seriesDesc:     all.seriesDescription || '',
      imageType:      all.imageType || '',

      kvp:            all.kvp ?? null,
      mA:             all.xRayTubeCurrent ?? null,
      mAs:            all.exposure ?? null,
      exposureTime:   all.exposureTime ?? null,
      sliceThickness: all.sliceThickness ?? null,
      sliceSpacing:   all.spacingBetweenSlices ?? null,
      kernel:         all.convolutionKernel || '',
      fov:            all.reconstructionDiameter ?? null,
      pitch:          all.spiralPitchFactor ?? null,
      gantryTilt:     all.gantryDetectorTilt ?? null,

      rows:           all.rows ?? null,
      columns:        all.columns ?? null,
      pixelSpacingX:  all.pixelSpacing?.[0] ?? all.rowPixelSpacing ?? null,
      pixelSpacingY:  all.pixelSpacing?.[1] ?? all.columnPixelSpacing ?? null,
      bitsAllocated:  all.bitsAllocated ?? null,
      bitsStored:     all.bitsStored ?? null,

      contrastAgent:  all.contrastBolusAgent || '',
      contrastRoute:  all.contrastBolusRoute || '',
      contrastVolume: all.contrastBolusTotalDose || '',
      hasContrast:    !!(all.contrastBolusAgent || all.contrastBolusRoute),

      windowCenter:   all.windowCenter ?? null,
      windowWidth:    all.windowWidth ?? null,

      cardiacSync:    all.cardiacSynchronizationTechnique || '',
      heartRate:      all.heartRate ?? null,
      triggerTime:    all.triggerTime ?? null,
      hasECGGating:   !!(all.cardiacSynchronizationTechnique || all.triggerTime != null || all.heartRate != null),

      manufacturer:   all.manufacturer || '',
      modelName:      all.manufacturerModelName || '',
      stationName:    all.stationName || '',
      softwareVer:    all.softwareVersions || '',
      institution:    all.institutionName || '',

      acqDate:        all.acquisitionDate || '',
      acqTime:        all.acquisitionTime || '',

      patientName:    all.patientName || '',
      patientId:      all.patientId || '',
      patientBirth:   all.patientBirthDate || '',
      patientSex:     all.patientSex || '',
      studyUID:       all.studyInstanceUID || '',

      ctdiVol:        all.ctdiVol ?? null,
      dlp:            all.dlp ?? null,
      dataCollDiam:   all.dataCollectionDiameter ?? null,
      tableHeight:    all.tableHeight ?? null,
      focalSpotSize:  all.focalSpotSize || '',
      rotationTime:   all.revolutionTime ?? all.rotationTime ?? null,
      singleCollim:   all.singleCollimationWidth ?? null,
      totalCollim:    all.totalCollimationWidth ?? null,

      contrastFlowRate: all.contrastFlowRate || '',
      contrastTotalDose: all.contrastBolusTotalDose || '',
      contrastStartTime: all.contrastBolusStartTime || '',
      contrastStopTime:  all.contrastBolusStopTime || '',

      reconPhasePercent: all.reconstructionPhasePercentage ?? null,
      rrInterval:     all.rrInterval ?? all.nominalInterval ?? null,
      reconAlgorithm: all.reconstructionAlgorithm || '',
      sopClassUID:    all.sopClassUID || '',
    };
  }

  // ─── Классификация ядра реконструкции ────────────────────

  static classifyKernel(kernel) {
    if (!kernel) return { type: 'unknown', label: 'Не определён' };
    const k = kernel.toUpperCase();

    const soft = ['B10','B20','B25','B26','B30','B31','B35','B36','FC01','FC02','FC03','FC10','FC11','FC12','FC13','STANDARD','SOFT','SA36','BF36','BF40','BODY','J30','J36','D20','I26','I30','QR40','VA40','VA36'];
    const medium = ['B40','B41','B43','B45','B46','FC50','FC51','FC52','DETAIL','STND','J40','J41','I40','I41','D33','D36','D40','VA46','QR44','SA40'];
    const hard = ['B50','B60','B70','B75','B80','FC80','FC81','FC82','FC83','FC84','FC85','FC86','BONE','LUNG','BONEPLUS','CHEST','SHARP','EDGE','BL57','BL64','BL65','B157','H60','H70','J70','J80','I50','I70','D50','D60','D70','YA','YB','VA70','VA80','QR59','QR66','SA50'];

    for (const s of soft)   if (k.includes(s)) return { type: 'soft', label: 'Мягкий (сосуды/мягкие ткани)' };
    for (const m of medium) if (k.includes(m)) return { type: 'medium', label: 'Средний (универсальный)' };
    for (const h of hard)   if (k.includes(h)) return { type: 'hard', label: 'Жёсткий (лёгкие/кости)' };

    return { type: 'unknown', label: kernel };
  }

  // ─── Фаза контрастирования ──────────────────────────────

  /**
   * [12.0.24] БЫЛ ЛИ КОНТРАСТ — и откуда это известно.
   *
   * Прежде контраст признавался ТОЛЬКО по тегам ContrastBolusAgent (0018,0010)
   * и ContrastBolusRoute (0018,1040). Canon/Toshiba их часто не заполняет —
   * и серия «Body 1.0 CE» с пустыми тегами объявлялась нативной. Описание
   * серии шло в дело лишь для названия фазы, и то после «да» от тегов.
   *
   * Теперь по порядку доверия:
   *   1. теги — если заполнены по-настоящему («NONE», «NO», «-» — не в счёт);
   *   2. описание СЕРИИ и протокол — отрицание («без контраста», NC, PRE,
   *      NATIVE) проверяется ПЕРВЫМ, иначе «NON CONTRAST» сошёл бы за контраст;
   *   3. признаков нет — «нативная», но с оговоркой, что это вывод из
   *      отсутствия, а не прямое указание.
   *
   * Описание ИССЛЕДОВАНИЯ не используется: оно общее для всех серий, а в
   * одном исследовании бывают и нативная, и контрастные серии.
   *
   * @returns {{есть: boolean, источник: 'теги'|'описание'|'нет признаков', основание: string}}
   */
  static решитьКонтраст(p) {
    const пусто = (в) => !в || /^(NONE|NO|N\/?A|-|0|НЕТ)$/i.test(String(в).trim());
    const теги = !пусто(p.contrastAgent) || !пусто(p.contrastRoute);
    const описание = [p.seriesDesc, p.protocolName].filter(Boolean).join(' · ');
    const т = описание.toUpperCase();
    const НЕТ = /NON[\s-]?CONTRAST|\bNC\b|\bW\/?O\b|WITHOUT|\bPRE\b|\bPLAIN\b|UNENHANC|\bNATIVE?\b|БЕЗ\s*КОНТР|НАТИВ/;
    const ДА = /\bCE\b|\bC\+|\+C\b|CONTRAST|ENHANC|\bARTERIAL\b|\bART\b|\bPORTAL\b|\bPV\b|\bVENOUS\b|\bDELAY(ED)?\b|NEPHRO|EXCRET|\bCTA\b|КОНТРАСТ|АРТЕР|ПОРТАЛ|ВЕНОЗ|ОТСРОЧ/;
    if (теги) {
      return { есть: true, источник: 'теги', основание: 'заполнены теги контраста (0018,0010)/(0018,1040)' };
    }
    if (НЕТ.test(т)) {
      return { есть: false, источник: 'описание', основание: `теги контраста пусты; в описании «${описание}» — без контраста` };
    }
    if (ДА.test(т)) {
      return { есть: true, источник: 'описание', основание: `теги контраста пусты; в описании «${описание}» — признак контраста` };
    }
    return { есть: false, источник: 'нет признаков', основание: 'теги контраста пусты, в описании серии признаков контраста нет' };
  }

  static detectContrastPhase(params) {
    const text = [params.seriesDesc, params.protocolName, params.studyDesc].join(' ').toUpperCase();
    if (!params.hasContrast) {
      // [12.0.24] «Нативная» бывает прямым указанием, а бывает выводом из
      // отсутствия признаков. Врачу эта разница важна — говорим, какая.
      return params.contrastSource?.источник === 'нет признаков'
        ? { phase: 'native', label: 'Нативная — вероятно: признаков контраста нет' }
        : { phase: 'native', label: 'Нативная (без контраста)' };
    }
    
    // Используем закэшированные регулярки (PRO-FEATURE 1)
    if (RX_CACHE.phaseArterial.test(text))  return { phase: 'arterial', label: 'Артериальная фаза' };
    if (RX_CACHE.phaseVenous.test(text))    return { phase: 'venous', label: 'Венозная фаза' };
    if (RX_CACHE.phaseDelayed.test(text))   return { phase: 'delayed', label: 'Отложенная фаза' };
    if (RX_CACHE.phasePulm.test(text))      return { phase: 'pulmonary', label: 'Лёгочная артериальная фаза (ТЭЛА)' };
    
    return { phase: 'contrast', label: 'С контрастом (фаза не определена)' };
  }

  // ─── Классификация сканера ──────────────────────────────

  static classifyScanner(params) {
    const model = (params.modelName || '').toUpperCase();
    const mfr = (params.manufacturer || '').toUpperCase();

    let brand = 'Unknown';
    if (mfr.includes('SIEMENS') || mfr.includes('SOMATOM')) brand = 'Siemens';
    else if (mfr.includes('GE') || mfr.includes('GENERAL ELECTRIC')) brand = 'GE';
    else if (mfr.includes('PHILIPS')) brand = 'Philips';
    else if (mfr.includes('TOSHIBA') || mfr.includes('CANON')) brand = 'Canon/Toshiba';
    else if (mfr.includes('UNITED') || mfr.includes('UIH')) brand = 'United Imaging';

    let tier = 'standard';
    const premium = ['FORCE','FLASH','DRIVE','EDGE','DEFINITION','REVOLUTION','APEX','CT750','CT870','DISCOVERY','IQON','SPECTRAL','BRILLIANCE ICT','INCISIVE','AQUILION ONE','AQUILION PRECISION','GENESIS','UCT 960','UCT 780'];
    for (const p of premium) if (model.includes(p)) { tier = 'premium'; break; }

    const isDualSource = model.includes('FLASH') || model.includes('FORCE') || model.includes('DRIVE');

    let iterRecon = 'Не определено';
    if (brand === 'Siemens')        iterRecon = tier === 'premium' ? 'ADMIRE / TurboFlash' : 'SAFIRE / IRIS';
    else if (brand === 'GE')        iterRecon = tier === 'premium' ? 'TrueFidelity (DL)' : 'ASIR-V / Veo';
    else if (brand === 'Philips')   iterRecon = 'iDose⁴ / IMR';
    else if (brand === 'Canon/Toshiba') iterRecon = 'AIDR 3D / AiCE (DL)';

    return { brand, model: params.modelName, tier, isDualSource, iterRecon };
  }

  // ─── Дозиметрия ──────────────────────────────────────────

  static calcDose(params) {
    const ctdi = params.ctdiVol;
    const dlp = params.dlp;
    const kFactors = { 'HEAD': 0.0021, 'BRAIN': 0.0021, 'CHEST': 0.014, 'THORAX': 0.014, 'ABDOMEN': 0.015, 'PELVIS': 0.015, 'SPINE': 0.015, 'NECK': 0.0059 };
    const k = kFactors[params.bodyPart] || 0.015;
    const effectiveDose = dlp !== null ? +(dlp * k).toFixed(1) : null;

    let level = 'unknown';
    if (effectiveDose !== null) {
      if (effectiveDose < 3) level = 'low';
      else if (effectiveDose < 10) level = 'moderate';
      else if (effectiveDose < 20) level = 'high';
      else level = 'very_high';
    }

    return { ctdi, dlp, effectiveDose, k, level };
  }

  // ─── Временнóе разрешение ────────────────────────────────

  static calcTemporalResolution(params, scanner) {
    const rot = params.rotationTime;
    if (rot === null) return { temporal: null, maxHR: null, label: 'Не определено' };
    const temporal = scanner.isDualSource ? Math.round(rot * 1000 / 4) : Math.round(rot * 1000 / 2);
    const maxHR = temporal <= 75 ? 120 : temporal <= 100 ? 100 : temporal <= 150 ? 80 : temporal <= 200 ? 65 : 55;
    const label = `${temporal} мс${scanner.isDualSource ? ' (Dual Source)' : ''}`;
    return { temporal, maxHR, label };
  }

  // ─── Детектор (рядность) ─────────────────────────────────

  static detectDetectorRows(params) {
    const sc = params.singleCollim;
    const tc = params.totalCollim;
    if (sc && tc && sc > 0) {
      const rows = Math.round(tc / sc);
      return { rows, collimation: `${rows}×${sc} мм = ${tc} мм`, label: `${rows}-рядный` };
    }
    return { rows: null, collimation: null, label: 'Не определено' };
  }

  // ─── Метод реконструкции ─────────────────────────────────

  static classifyRecon(params, scanner) {
    const algo = (params.reconAlgorithm || '').toUpperCase();
    const sw = (params.softwareVer || '').toUpperCase();
    const text = algo + ' ' + sw;

    // Используем закэшированные регулярки (PRO-FEATURE 1)
    if (RX_CACHE.reconDL.test(text)) return { type: 'dl', label: 'Deep Learning (DL)', icon: '🤖' };
    if (RX_CACHE.reconIterative.test(text)) return { type: 'iterative', label: 'Итеративная', icon: '🔄' };
    if (RX_CACHE.reconMBIR.test(text)) return { type: 'mbir', label: 'Model-Based (MBIR)', icon: '🧠' };
    if (RX_CACHE.reconFBP.test(text)) return { type: 'fbp', label: 'FBP (стандарт)', icon: '📐' };

    if (scanner.tier === 'premium') return { type: 'iterative', label: scanner.iterRecon, icon: '🔄' };
    return { type: 'unknown', label: 'Не определён', icon: '❓' };
  }

  // ─── Тип изображения (фильтрация) ───────────────────────

  static classifyImageType(params) {
    const it = (params.imageType || '').toUpperCase();
    const sop = params.sopClassUID || '';

    const isLocalizer = it.includes('LOCALIZER') || it.includes('SCOUT');
    const isSecondary = it.includes('SECONDARY') || sop === '1.2.840.10008.5.1.4.1.1.7';
    const isDerived = it.includes('DERIVED');
    const isAxial = it.includes('AXIAL');
    const isReformat = it.includes('REFORMAT') || it.includes('MPR');

    let label = 'Аксиальный';
    if (isLocalizer) label = 'Скаут (локалайзер)';
    else if (isSecondary) label = 'Вторичный захват';
    else if (isDerived && isReformat) label = 'Реформат (MPR)';
    else if (isDerived) label = 'Производное';
    else if (isAxial) label = 'Аксиальный (оригинал)';

    return { isLocalizer, isSecondary, isDerived, isAxial, isReformat, label };
  }

  // ═══════════════════════════════════════════════════════════
  //  ГЛАВНЫЙ МЕТОД: Полный анализ
  // ═══════════════════════════════════════════════════════════

  static analyze(params) {
    if (params.modality !== 'CT') {
      return {
        params, isCT: false,
        summary: `Модальность: ${params.modality || 'не определена'}. Анализ качества доступен только для КТ.`,
        capabilities: [], limitations: [], recommendations: [],
      };
    }

    // [12.0.24] Контраст решается ОДИН раз и до всех выводов: от него зависят
    // и фаза, и половина «что можно оценить» (КТА, CPR, сосуды).
    const контраст = this.решитьКонтраст(params);
    params = { ...params, hasContrast: контраст.есть, contrastSource: контраст };

    const kernel = this.classifyKernel(params.kernel);
    const phase = this.detectContrastPhase(params);
    const scanner = this.classifyScanner(params);
    const dose = this.calcDose(params);
    const temporal = this.calcTemporalResolution(params, scanner);
    const detector = this.detectDetectorRows(params);
    const recon = this.classifyRecon(params, scanner);
    const imgType = this.classifyImageType(params);
    
    // [PRO-FEATURE 3]: Smart Diagnostic Fallback.
    // Если толщина среза не указана, но есть интервал (spacing), берем его как ориентир.
    const st = params.sliceThickness ?? params.sliceSpacing; 
    
    const hasContrast = params.hasContrast;
    const hasECG = params.hasECGGating;
    const pxSize = params.pixelSpacingX;

    // ─── Оценка шума ─────────────────────────────────────
    let noiseScore = 50;
    if (st !== null) {
      if (st <= 0.5) noiseScore += 25;
      else if (st <= 1.0) noiseScore += 15;
      else if (st <= 2.0) noiseScore += 5;
      else if (st >= 5.0) noiseScore -= 15;
    }
    if (params.kvp !== null) {
      if (params.kvp <= 80) noiseScore += 15;
      else if (params.kvp <= 100) noiseScore += 5;
      else if (params.kvp >= 140) noiseScore -= 10;
    }
    if (params.mAs !== null) {
      if (params.mAs < 50) noiseScore += 20;
      else if (params.mAs < 100) noiseScore += 10;
      else if (params.mAs > 300) noiseScore -= 15;
      else if (params.mAs > 200) noiseScore -= 10;
    }
    if (kernel.type === 'hard') noiseScore += 15;
    else if (kernel.type === 'soft') noiseScore -= 10;

    if (recon.type === 'dl') noiseScore -= 20;
    else if (recon.type === 'mbir') noiseScore -= 18;
    else if (recon.type === 'iterative') noiseScore -= 12;

    const noiseLevel = noiseScore >= 60 ? 'high' : noiseScore <= 30 ? 'low' : 'moderate';

    // ─── Возможности и ограничения ────────────────────────
    const capabilities = [];
    const limitations = [];
    const recommendations = [];

    // 1. Calcium Score
    if (!hasContrast && st !== null && st >= 2.5 && st <= 3.5) {
      capabilities.push({ icon: '✅', feature: 'Calcium Score (Agatston)', detail: `Нативно, срез ${st} мм — стандартный протокол` });
    } else if (!hasContrast && st !== null && st < 2.5) {
      capabilities.push({ icon: '⚠️', feature: 'Calcium Score (нестандартный)', detail: `Срез ${st} мм тоньше 3 мм → Agatston может быть занижен` });
    } else if (hasContrast) {
      limitations.push({ icon: '❌', feature: 'Calcium Score', detail: 'Контраст мешает. Нужно нативное, 3 мм, 120 kVp' });
    } else {
      limitations.push({ icon: '⚠️', feature: 'Calcium Score', detail: st ? `Срез ${st} мм, стандарт = 3 мм` : 'Толщина среза не определена' });
    }

    // 2. Коронарные артерии
    if (hasContrast && st !== null && st <= 1.0 && hasECG) {
      capabilities.push({ icon: '✅', feature: 'КТ-коронарография', detail: `Срез ${st} мм + контраст + ECG — полная оценка коронаров` });
    } else if (hasContrast && st !== null && st <= 1.5 && hasECG) {
      capabilities.push({ icon: '⚠️', feature: 'КТ-коронарография (ограниченно)', detail: `Срез ${st} мм — мелкие ветви <2 мм могут быть не видны` });
    } else if (hasContrast && st !== null && st <= 1.0 && !hasECG) {
      limitations.push({ icon: '⚠️', feature: 'Коронарные артерии', detail: 'Нет ECG-gating → артефакты движения. Крупные стволы видны, мелкие нет' });
    } else {
      const r = [];
      if (!hasContrast) r.push('нет контраста');
      if (!hasECG) r.push('нет ECG-gating');
      if (st && st > 1.5) r.push(`срез ${st} мм`);
      limitations.push({ icon: '❌', feature: 'КТ-коронарография', detail: r.join(', ') });
    }

    // 3. Аорта и крупные сосуды
    if (hasContrast && st !== null && st <= 3.0) {
      capabilities.push({ icon: '✅', feature: 'Аорта и крупные сосуды', detail: `Контраст + ${st} мм — аневризмы, диссекция, стенозы` });
    } else if (hasContrast && st && st <= 5.0) {
      capabilities.push({ icon: '⚠️', feature: 'Аорта (грубо)', detail: `Срез ${st} мм — только крупные патологии` });
    } else if (!hasContrast) {
      limitations.push({ icon: '⚠️', feature: 'Сосуды', detail: 'Без контраста видны только кальцинаты стенки' });
    }

    // 4. CPR / Выпрямление сосудов
    if (hasContrast && st !== null && st <= 1.0) {
      capabilities.push({ icon: '✅', feature: 'CPR (выпрямление сосудов)', detail: `${st} мм + контраст — идеально для CPR` });
    } else if (hasContrast && st && st <= 2.0) {
      capabilities.push({ icon: '⚠️', feature: 'CPR (ограниченно)', detail: `Срез ${st} мм — ступеньки на мелких сосудах` });
    } else {
      limitations.push({ icon: '❌', feature: 'CPR', detail: !hasContrast ? 'нет контраста' : `срез ${st || '?'} мм слишком толстый` });
    }

    // 5. Клапаны
    if (hasContrast && hasECG && st !== null && st <= 1.0) {
      capabilities.push({ icon: '✅', feature: 'Клапаны (TAVI-протокол)', detail: 'ECG + контраст + тонкие срезы — измерение кольца, створок' });
    } else {
      const r = [];
      if (!hasContrast) r.push('нет контраста');
      if (!hasECG) r.push('нет ECG');
      if (st > 1.0) r.push(`срез ${st} мм`);
      limitations.push({ icon: '❌', feature: 'Клапаны сердца', detail: r.join(', ') });
    }

    // 6. ТЭЛА
    if (hasContrast && phase.phase === 'pulmonary') {
      capabilities.push({ icon: '✅', feature: 'ТЭЛА', detail: 'Лёгочная фаза — оценка тромбов ЛА' });
    } else if (hasContrast && st && st <= 2.0) {
      capabilities.push({ icon: '⚠️', feature: 'Лёгочная артерия', detail: 'Контраст есть, но фаза может быть не оптимальной' });
    }

    // 7. Лёгкие
    if (st !== null && st <= 1.5 && (kernel.type === 'hard' || kernel.type === 'unknown')) {
      capabilities.push({ icon: '✅', feature: 'ВРКТ лёгких', detail: `${st} мм + ядро "${params.kernel}" — высокое разрешение` });
    } else if (st && st <= 3.0) {
      capabilities.push({ icon: '⚠️', feature: 'Лёгкие (стандарт)', detail: `${st} мм — мелкие интерстициальные изменения могут быть пропущены` });
    }

    // 8. MIP
    if (hasContrast && st && st <= 2.0) {
      capabilities.push({ icon: '✅', feature: 'MIP-реконструкция', detail: 'Контраст + тонкие срезы → сосудистое дерево' });
    } else if (!hasContrast && st && st <= 2.0) {
      capabilities.push({ icon: '⚠️', feature: 'MIP (ограниченно)', detail: 'Без контраста MIP покажет только кальцинаты' });
    }

    // 9. VRT
    if (hasContrast && st && st <= 1.5) {
      capabilities.push({ icon: '✅', feature: '3D Volume Rendering', detail: `${st} мм + контраст — хорошее 3D` });
    } else if (st && st <= 3.0) {
      capabilities.push({ icon: '⚠️', feature: '3D Volume Rendering', detail: `${st} мм — 3D возможен, качество среднее` });
    }

    // 10. Дозиметрия
    if (dose.effectiveDose !== null) {
      const dIcon = dose.level === 'low' ? '🟢' : dose.level === 'moderate' ? '🟡' : dose.level === 'high' ? '🟠' : '🔴';
      const dLabel = dose.level === 'low' ? 'низкая' : dose.level === 'moderate' ? 'умеренная' : dose.level === 'high' ? 'повышенная' : 'высокая';
      capabilities.push({ icon: dIcon, feature: `Доза: ${dLabel}`,
        detail: `CTDIvol: ${dose.ctdi ?? '?'} mGy, DLP: ${dose.dlp ?? '?'} mGy·cm → ~${dose.effectiveDose} мЗв` });
    } else if (dose.ctdi !== null) {
      capabilities.push({ icon: 'ℹ️', feature: 'Доза (частично)', detail: `CTDIvol: ${dose.ctdi} mGy, DLP не определён` });
    }

    // 11. Временное разрешение
    if (temporal.temporal !== null) {
      if (temporal.temporal <= 75) {
        capabilities.push({ icon: '✅', feature: `Временное разрешение ${temporal.label}`,
          detail: `Допустимый ЧСС ≤${temporal.maxHR} уд/мин — отлично для коронаров` });
      } else if (temporal.temporal <= 175) {
        capabilities.push({ icon: '⚠️', feature: `Временное разрешение ${temporal.label}`,
          detail: `Допустимый ЧСС ≤${temporal.maxHR} уд/мин — ЧСС >70 может давать артефакты` });
      } else {
        limitations.push({ icon: '❌', feature: `Временное разрешение ${temporal.label}`,
          detail: `Только ЧСС ≤${temporal.maxHR} уд/мин. Для коронаров нужен более быстрый сканер.` });
      }
    }

    // 12. Фаза сердечного цикла
    if (params.reconPhasePercent !== null) {
      const pct = params.reconPhasePercent;
      const phaseLabel = pct >= 70 && pct <= 80 ? 'диастола (оптимально)' : pct >= 30 && pct <= 45 ? 'систола' : `${pct}% R-R`;
      capabilities.push({ icon: '💓', feature: `Кардио-фаза: ${phaseLabel}`,
        detail: `Реконструкция при ${pct}% R-R${params.heartRate ? `, ЧСС ${params.heartRate}` : ''}` });
    }

    // 13. Детектор
    if (detector.rows !== null) {
      const dIcon = detector.rows >= 128 ? '✅' : detector.rows >= 64 ? '⚠️' : 'ℹ️';
      capabilities.push({ icon: dIcon, feature: `Детектор: ${detector.label}`,
        detail: `Коллимация: ${detector.collimation}` });
    }

    // 14. Реконструкция — контекстные подсказки
    if (recon.type === 'dl') {
      capabilities.push({ icon: '🤖', feature: `Реконструкция: ${recon.label}`,
        detail: 'Максимальное подавление шума с сохранением текстуры тканей. Наиболее достоверная оценка мелких структур.' });
      if (hasContrast && st && st <= 1.0) {
        capabilities.push({ icon: '🤖', feature: 'DL + тонкие срезы + контраст',
          detail: 'Мелкие сосуды и бляшки видны с высокой достоверностью. Текстура бляшек сохранена — можно оценивать состав (мягкие/кальцинированные).' });
      }
      if (kernel.type === 'hard') {
        capabilities.push({ icon: '🤖', feature: 'DL + жёсткое ядро',
          detail: 'DL компенсирует шум жёсткого ядра. Лёгкие и кости в высоком разрешении без зернистости.' });
      }
    } else if (recon.type === 'iterative') {
      capabilities.push({ icon: '🔄', feature: `Реконструкция: ${recon.label}`,
        detail: 'Снижение шума на 30-60% при той же дозе. Тонкие срезы становятся диагностичными.' });
      if (hasContrast && st && st <= 1.0) {
        capabilities.push({ icon: '🔄', feature: 'Итеративная + тонкие срезы + контраст',
          detail: 'Шум подавлен, мелкие сосуды должны быть видны. Если текстура бляшки выглядит неестественно гладкой — это артефакт реконструкции, а не истинная морфология.' });
      }
      if (kernel.type === 'hard' && ['CHEST'].includes(params.bodyPart)) {
        recommendations.push('🔄 Итеративная + жёсткое ядро (лёгкие): может сглаживать интерстициальные изменения. Для ВРКТ запросите серию с FBP для сравнения.');
      }
      if (kernel.type === 'soft' && hasContrast) {
        capabilities.push({ icon: '🔄', feature: 'Итеративная + мягкое ядро',
          detail: 'Оптимально для оценки мягких тканей и сосудов. Низкий шум, плавные контуры.' });
      }
      if (noiseLevel === 'high') {
        recommendations.push('🔄 Несмотря на итеративную реконструкцию, шум высокий. Используйте MIP-слэбы 5-10 мм для оценки сосудов и средостения.');
      }
    } else if (recon.type === 'mbir') {
      capabilities.push({ icon: '🧠', feature: `Реконструкция: ${recon.label}`,
        detail: 'Максимальное подавление шума (до 80%). Отлично для ультранизкодозовых протоколов.' });
      if (st && st <= 0.5) {
        capabilities.push({ icon: '🧠', feature: 'MBIR + ультратонкие срезы',
          detail: `${st} мм — субмиллиметровые срезы диагностичны благодаря MBIR. Оценка мелких ветвей и стентов.` });
      }
      recommendations.push('🧠 MBIR может создавать «восковой» вид тканей. Сравнивайте с итеративной серией при оценке текстуры паренхимы.');
    } else if (recon.type === 'fbp') {
      limitations.push({ icon: '📐', feature: `Реконструкция: FBP (стандарт)`,
        detail: 'Без подавления шума. На тонких срезах при низкой дозе шум может маскировать мелкие структуры.' });
      if (st && st <= 1.0 && noiseLevel === 'high') {
        recommendations.push('📐 FBP + тонкие срезы + высокий шум: мелкие сосуды и интерстиций плохо видны. Используйте MIP 3-5 мм или запросите итеративную реконструкцию.');
      }
      if (hasContrast && st && st <= 1.5) {
        recommendations.push('📐 FBP + контраст: для оценки бляшек используйте широкое окно (W:1500/L:500) — шум менее заметен на высоком контрасте.');
      }
      if (kernel.type === 'hard') {
        capabilities.push({ icon: 'ℹ️', feature: 'FBP + жёсткое ядро',
          detail: 'Максимальная пространственная разрешающая способность. Идеально для лёгких и костей, но шум выражен.' });
      }
      if (params.kvp && params.kvp <= 100) {
        recommendations.push('📐 Низкий kVp + FBP → повышенный шум. Рекомендуется итеративная или DL реконструкция для сохранения диагностического качества.');
      }
    } else {
      capabilities.push({ icon: '❓', feature: 'Реконструкция: не определена',
        detail: 'Алгоритм реконструкции не записан в DICOM. Оценка шума основана на параметрах протокола.' });
    }

    // 15. Контраст расширенный
    if (hasContrast && params.contrastFlowRate) {
      const rate = parseFloat(params.contrastFlowRate);
      if (rate >= 4.0) {
        capabilities.push({ icon: '✅', feature: `Болюс ${rate} мл/с`,
          detail: `Высокая скорость → хорошее контрастирование артерий${params.contrastTotalDose ? `, объём ${params.contrastTotalDose} мл` : ''}` });
      } else if (rate >= 2.5) {
        capabilities.push({ icon: '⚠️', feature: `Болюс ${rate} мл/с`,
          detail: `Средняя скорость${params.contrastTotalDose ? `, объём ${params.contrastTotalDose} мл` : ''}` });
      } else {
        limitations.push({ icon: '⚠️', feature: `Болюс ${rate} мл/с`,
          detail: 'Низкая скорость → контрастирование мелких сосудов может быть недостаточным' });
      }
    }

    // 16. Тип изображения
    if (imgType.isLocalizer) {
      limitations.push({ icon: '⚠️', feature: 'Скаут-изображение', detail: 'Это локалайзер (топограмма), не диагностические срезы' });
    } else if (imgType.isSecondary) {
      limitations.push({ icon: '⚠️', feature: 'Вторичный захват', detail: 'Screen capture / вторичное изображение, не оригинальные данные' });
    }

    // 17. Центровка пациента
    if (params.tableHeight !== null) {
      const th = Math.abs(params.tableHeight);
      if (th > 30) {
        recommendations.push(`📐 Стол смещён на ${params.tableHeight.toFixed(0)} мм от изоцентра. Шум повышен на ~${Math.round(th * 0.3)}%.`);
      }
    }

    // ─── Рекомендации ─────────────────────────────────────
    if (noiseLevel === 'high') recommendations.push('⚡ Высокий шум. Используйте MIP-слэбы 5-10 мм и сглаживающие фильтры.');
    if (kernel.type === 'hard' && params.bodyPart !== 'CHEST') recommendations.push(`🔧 Жёсткое ядро (${params.kernel}). Для сосудов лучше серия с мягким ядром.`);
    if (st && st > 3.0 && hasContrast) recommendations.push(`📏 Толстые срезы (${st} мм). Запросите тонкосрезовую реконструкцию 0.5-1.0 мм.`);
    if (!hasContrast && ['CHEST','ABDOMEN',''].includes(params.bodyPart)) recommendations.push('💉 Нативное исследование. Для сосудов нужна КТ-ангиография.');
    if (hasContrast && !hasECG && ['CHEST',''].includes(params.bodyPart)) recommendations.push('💓 Нет ECG-gating. Для коронаров/клапанов нужен ЭКГ-синхронизированный протокол.');
    if (pxSize && pxSize > 0.8) recommendations.push(`🔍 Пиксель ${pxSize.toFixed(2)} мм — крупный. Уменьшите FOV или Target Recon.`);
    if (dose.level === 'very_high') recommendations.push(`☢️ Высокая доза (~${dose.effectiveDose} мЗв). Проверьте обоснование и используйте итеративную/DL реконструкцию.`);
    if (temporal.temporal && temporal.temporal > 150 && hasECG) recommendations.push(`⏱ Временное разрешение ${temporal.temporal} мс — недостаточно. Для коронаров используйте бета-блокаторы для ЧСС <65.`);

    const summary = this._buildSummary(params, kernel, phase, scanner, noiseLevel, st, dose, temporal, detector, recon);

    return { params, isCT: true, kernel, phase, scanner, dose, temporal, detector, recon, imgType, noiseLevel, capabilities, limitations, recommendations, summary };
  }

  // ─── Текстовая сводка ───────────────────────────────────

  static _buildSummary(p, kernel, phase, scanner, noise, st, dose, temporal, detector, recon) {
    const L = [];
    if (p.patientName || p.patientId) L.push(`👤 ${p.patientName || 'Неизвестно'}${p.patientId ? ` (ID: ${p.patientId})` : ''}${p.patientSex ? `, ${p.patientSex}` : ''}${p.patientBirth ? `, ДР: ${p.patientBirth}` : ''}`);
    L.push(`🏥 ${scanner.brand} ${scanner.model || '?'} (${p.institution || '—'})`);
    if (scanner.isDualSource) L.push('  ⚡ Dual Source');
    if (detector.rows !== null) L.push(`  📡 Детектор: ${detector.label}, коллим. ${detector.collimation}`);
    if (recon.type !== 'unknown') L.push(`  ${recon.icon} Реконструкция: ${recon.label}`);
    L.push('');
    L.push(`📋 Протокол: ${p.protocolName || p.seriesDesc || p.studyDesc || '—'}`);
    L.push(`🎯 Область: ${p.bodyPart || '—'}`);
    L.push(`💉 Контраст: ${phase.label}`);
    if (p.contrastAgent) L.push(`   Агент: ${p.contrastAgent}`);
    if (p.contrastFlowRate) L.push(`   Скорость: ${p.contrastFlowRate} мл/с${p.contrastTotalDose ? `, объём: ${p.contrastTotalDose} мл` : ''}`);
    if (p.hasECGGating) {
      L.push(`💓 ECG-gating: да${p.heartRate ? ` (ЧСС ${p.heartRate} уд/мин)` : ''}`);
      if (p.reconPhasePercent !== null) L.push(`   Фаза реконструкции: ${p.reconPhasePercent}% R-R`);
    }
    if (temporal.temporal !== null) L.push(`⏱ Временнóе разрешение: ${temporal.label} (ЧСС ≤${temporal.maxHR})`);
    L.push('');
    L.push('── Параметры ──');
    if (p.kvp !== null)    L.push(`  kVp: ${p.kvp}`);
    if (p.mA !== null)     L.push(`  мА: ${p.mA}`);
    if (p.mAs !== null)    L.push(`  мАс: ${p.mAs}`);
    if (st !== null)       L.push(`  Срез: ${st} мм`);
    if (p.sliceSpacing)    L.push(`  Интервал: ${p.sliceSpacing} мм`);
    if (p.kernel)          L.push(`  Ядро: ${p.kernel} → ${kernel.label}`);
    if (p.fov)             L.push(`  FOV: ${p.fov} мм`);
    if (p.pitch)           L.push(`  Pitch: ${p.pitch}`);
    if (p.rotationTime)    L.push(`  Ротация: ${p.rotationTime} с`);
    if (p.pixelSpacingX)   L.push(`  Пиксель: ${p.pixelSpacingX.toFixed(3)}×${(p.pixelSpacingY||p.pixelSpacingX).toFixed(3)} мм`);
    L.push(`  Матрица: ${p.rows||'?'}×${p.columns||'?'}`);
    L.push('');
    if (dose.effectiveDose !== null) {
      const dIcon = dose.level === 'low' ? '🟢' : dose.level === 'moderate' ? '🟡' : dose.level === 'high' ? '🟠' : '🔴';
      L.push(`☢️ Доза: CTDIvol ${dose.ctdi ?? '?'} mGy | DLP ${dose.dlp ?? '?'} mGy·cm | ~${dose.effectiveDose} мЗв ${dIcon}`);
    }
    L.push(`📊 Шум: ${noise === 'low' ? '🟢 низкий' : noise === 'moderate' ? '🟡 умеренный' : '🔴 высокий'}`);
    return L.join('\n');
  }

  // ═══════════════════════════════════════════════════════════
  //  HTML-панель для UI
  // ═══════════════════════════════════════════════════════════

  // [PRO-FEATURE 4]: Advanced XSS Protection (экранирует кавычки)
  static _esc(s) {
    if (!s) return '';
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  static renderHTML(analysis) {
    if (!analysis.isCT) {
      return `<div class="sq-panel"><p>Модальность: <b>${this._esc(analysis.params.modality) || '?'}</b>. Анализ доступен только для КТ.</p></div>`;
    }

    const card = (items, bgColor, borderColor) => items.map(c => `
      <div style="background:${bgColor};border:1px solid ${borderColor};border-radius:6px;padding:8px 10px;margin-bottom:4px;">
        <div style="font-weight:600;">${iconizeStatus(c.icon)} ${this._esc(c.feature)}</div>
        <div style="color:#a0a0b0;font-size:12px;margin-top:2px;">${this._esc(c.detail)}</div>
      </div>`).join('');

    return `
<div class="sq-panel" style="font-family:system-ui,sans-serif;color:#e0e0e8;font-size:13px;line-height:1.5;">
  <div style="font-size:16px;font-weight:700;margin-bottom:12px;color:#fff;">📊 Анализ качества исследования</div>
  <div style="background:#1a1a2e;border-radius:8px;padding:12px;margin-bottom:12px;white-space:pre-line;font-family:'Fira Code',monospace;font-size:11px;border:1px solid #2a2a40;">${this._esc(analysis.summary)}</div>
  <div style="margin-bottom:10px;">
    <div style="font-size:14px;font-weight:600;color:#4aff8a;margin-bottom:6px;">✅ Что можно оценить</div>
    ${card(analysis.capabilities, '#0d2818', '#1a4028')}
  </div>
  ${analysis.limitations.length ? `
  <div style="margin-bottom:10px;">
    <div style="font-size:14px;font-weight:600;color:#ff6a6a;margin-bottom:6px;">❌ Ограничения</div>
    ${card(analysis.limitations, '#2a0d0d', '#402020')}
  </div>` : ''}
  ${analysis.recommendations.length ? `
  <div>
    <div style="font-size:14px;font-weight:600;color:#ffc846;margin-bottom:6px;">💡 Рекомендации</div>
    ${analysis.recommendations.map(r => `
    <div style="background:#2a2200;border:1px solid #403800;border-radius:6px;padding:8px 10px;margin-bottom:4px;font-size:12px;">${this._esc(r)}</div>`).join('')}
  </div>` : ''}
</div>`;
  }

  static renderMultiPatientHTML(patientsMap) {
    const patients = Object.values(patientsMap);
    if (patients.length === 0) return '<div class="sq-panel"><p>Нет данных для анализа</p></div>';
    if (patients.length === 1) return this.renderHTML(patients[0].analyses[0]);

    // [PRO-FEATURE 5]: Scoped SPA UI. Генерация уникального ID защищает обработчики событий от коллизий в React/Vue.
    const panelId = 'sq-panel-' + Math.random().toString(36).substr(2, 9);
    const colors = ['#4fc3f7','#81c784','#ffb74d','#f06292','#ba68c8','#4dd0e1','#aed581','#ff8a65'];

    let tabsHTML = '';
    let panelsHTML = '';

    patients.forEach((pat, i) => {
      const col = colors[i % colors.length];
      const name = pat.name || `Пациент ${i + 1}`;
      const active = i === 0;
      const analysis = pat.analyses[0];
      const badge = analysis.noiseLevel === 'high' ? '🔴' : analysis.noiseLevel === 'low' ? '🟢' : '🟡';
      const seriesCount = pat.analyses.length;

      tabsHTML += `<button class="sq-mp-tab${active ? ' sq-mp-active' : ''}" data-sq-tab="${i}"
        style="padding:6px 12px;border:none;border-bottom:2px solid ${active ? col : 'transparent'};
        background:${active ? 'rgba(255,255,255,0.08)' : 'transparent'};color:${active ? col : '#888'};
        cursor:pointer;font-size:12px;font-weight:600;white-space:nowrap;border-radius:4px 4px 0 0;transition:all 0.2s;"
        onmouseenter="this.style.color='${col}'" onmouseleave="if(!this.classList.contains('sq-mp-active'))this.style.color='#888'">
        ${iconizeStatus(badge)} ${this._esc(name.split('^')[0])}${seriesCount > 1 ? ` (${seriesCount})` : ''}
      </button>`;

      const card = (items, bgColor, borderColor) => items.map(c => `
        <div style="background:${bgColor};border:1px solid ${borderColor};border-radius:6px;padding:8px 10px;margin-bottom:4px;">
          <div style="font-weight:600;">${iconizeStatus(c.icon)} ${this._esc(c.feature)}</div>
          <div style="color:#a0a0b0;font-size:12px;margin-top:2px;">${this._esc(c.detail)}</div>
        </div>`).join('');

      panelsHTML += `<div class="sq-mp-panel" data-sq-panel="${i}" style="display:${active ? 'block' : 'none'};">
        <div style="padding:4px 0 8px;font-size:11px;color:#888;">
          ${pat.id ? `ID: ${this._esc(pat.id)}` : ''}${pat.sex ? ` | ${this._esc(pat.sex)}` : ''}${pat.birth ? ` | ДР: ${this._esc(pat.birth)}` : ''}
          ${seriesCount > 1 ? ` | Серий: ${seriesCount}` : ''}
        </div>
        <div style="background:#1a1a2e;border-radius:8px;padding:12px;margin-bottom:12px;white-space:pre-line;font-family:'Fira Code',monospace;font-size:11px;border:1px solid #2a2a40;">${this._esc(analysis.summary)}</div>
        <div style="margin-bottom:10px;">
          <div style="font-size:14px;font-weight:600;color:#4aff8a;margin-bottom:6px;">✅ Что можно оценить</div>
          ${card(analysis.capabilities, '#0d2818', '#1a4028')}
        </div>
        ${analysis.limitations.length ? `
        <div style="margin-bottom:10px;">
          <div style="font-size:14px;font-weight:600;color:#ff6a6a;margin-bottom:6px;">❌ Ограничения</div>
          ${card(analysis.limitations, '#2a0d0d', '#402020')}
        </div>` : ''}
        ${analysis.recommendations.length ? `
        <div>
          <div style="font-size:14px;font-weight:600;color:#ffc846;margin-bottom:6px;">💡 Рекомендации</div>
          ${analysis.recommendations.map(r => `
          <div style="background:#2a2200;border:1px solid #403800;border-radius:6px;padding:8px 10px;margin-bottom:4px;font-size:12px;">${this._esc(r)}</div>`).join('')}
        </div>` : ''}
      </div>`;
    });

    return `
<div id="${panelId}" class="sq-panel" style="font-family:system-ui,sans-serif;color:#e0e0e8;font-size:13px;line-height:1.5;">
  <div style="font-size:16px;font-weight:700;margin-bottom:4px;color:#fff;">📊 Анализ качества — ${patients.length} пациентов</div>
  <div style="display:flex;gap:2px;overflow-x:auto;border-bottom:1px solid #2a2a40;margin-bottom:12px;padding-bottom:0;">
    ${tabsHTML}
  </div>
  <div class="sq-mp-panels">${panelsHTML}</div>
</div>
<script>
(function(){
  // Изолируем поиск элементов только внутри сгенерированной панели, чтобы не ломать глобальный DOM
  let container = document.getElementById('${panelId}');
  if (!container) return;
  let tabs = container.querySelectorAll('.sq-mp-tab');
  let panels = container.querySelectorAll('.sq-mp-panel');
  
  tabs.forEach(function(tab){
    tab.addEventListener('click',function(){
      let idx = this.dataset.sqTab;
      tabs.forEach(function(t){
        t.classList.remove('sq-mp-active');
        t.style.background='transparent';
        t.style.color='#888';
        t.style.borderBottomColor='transparent';
      });
      this.classList.add('sq-mp-active');
      this.style.background='rgba(255,255,255,0.08)';
      this.style.color=this.style.borderBottomColor=getComputedStyle(this).color;
      panels.forEach(function(p){ p.style.display = p.dataset.sqPanel === idx ? 'block' : 'none'; });
    });
  });
})();
</script>`;
  }

  // ═══════════════════════════════════════════════════════════
  //  ENTERPRISE v8: Scoring, Protocol Compliance, Compare, Tasks
  // ═══════════════════════════════════════════════════════════

  /**
   * [v8] Числовая оценка качества (0-100) — для sorting / dashboards
   */
  static computeQualityScore(params) {
    if (!params || params.modality !== 'CT') return { score: 0, breakdown: {}, grade: 'N/A' };

    let score = 50;
    const breakdown = {};

    const st = params.sliceThickness ?? params.sliceSpacing;
    if (st !== null) {
      if (st <= 0.5)      breakdown.slice = 20;
      else if (st <= 1.0) breakdown.slice = 18;
      else if (st <= 1.5) breakdown.slice = 14;
      else if (st <= 3.0) breakdown.slice = 8;
      else if (st <= 5.0) breakdown.slice = 4;
      else                breakdown.slice = 0;
    } else { breakdown.slice = 5; }
    score += breakdown.slice - 10;

    const recon = this.classifyRecon(params, this.classifyScanner(params));
    if (recon.type === 'dl')             breakdown.recon = 15;
    else if (recon.type === 'mbir')      breakdown.recon = 13;
    else if (recon.type === 'iterative') breakdown.recon = 10;
    else if (recon.type === 'fbp')       breakdown.recon = 4;
    else                                 breakdown.recon = 5;
    score += breakdown.recon - 7;

    const dose = this.calcDose(params);
    if (dose.effectiveDose !== null) {
      if (dose.effectiveDose < 3)       breakdown.dose = 15;
      else if (dose.effectiveDose < 10) breakdown.dose = 10;
      else if (dose.effectiveDose < 20) breakdown.dose = 5;
      else                              breakdown.dose = 0;
    } else { breakdown.dose = 7; }
    score += breakdown.dose - 7;

    const px = params.pixelSpacingX;
    if (px !== null) {
      if (px <= 0.3)      breakdown.pixel = 10;
      else if (px <= 0.5) breakdown.pixel = 8;
      else if (px <= 0.8) breakdown.pixel = 5;
      else                breakdown.pixel = 2;
    } else { breakdown.pixel = 5; }
    score += breakdown.pixel - 5;

    breakdown.protocol = 0;
    if (params.hasContrast) breakdown.protocol += 3;
    if (params.hasECGGating) breakdown.protocol += 5;
    if (params.heartRate && params.heartRate < 70) breakdown.protocol += 2;
    score += breakdown.protocol;

    score = Math.max(0, Math.min(100, Math.round(score)));

    let grade;
    if (score >= 85)      grade = 'A';
    else if (score >= 70) grade = 'B';
    else if (score >= 50) grade = 'C';
    else if (score >= 30) grade = 'D';
    else                  grade = 'F';

    return { score, breakdown, grade };
  }

  /**
   * [v8] Проверка соответствия клиническому протоколу
   */
  static checkProtocolCompliance(params, protocol = 'auto') {
    const findings = [];
    if (params.modality !== 'CT') return { protocol: 'N/A', compliant: true, findings };

    if (protocol === 'auto') {
      if (params.hasECGGating) protocol = 'cardiac-cta';
      else if (params.hasContrast && (params.bodyPart === 'CHEST' || /ТЭЛА|PE\b/i.test(params.seriesDesc || ''))) protocol = 'pe-cta';
      else if (!params.hasContrast && params.sliceThickness >= 2.5 && params.sliceThickness <= 3.5) protocol = 'calcium-score';
      else if (params.hasContrast) protocol = 'contrast-ct';
      else protocol = 'non-contrast-ct';
    }

    const st = params.sliceThickness ?? params.sliceSpacing;
    const PROTOCOLS = {
      'cardiac-cta': {
        name: 'КТ-коронарография',
        rules: [
          { check: () => !params.hasContrast, msg: 'Требуется контрастное усиление', severity: 'error' },
          { check: () => !params.hasECGGating, msg: 'Требуется ECG-синхронизация', severity: 'error' },
          { check: () => st > 1.0, msg: `Толщина среза ${st} мм > 1.0 мм`, severity: 'error' },
          { check: () => st > 0.75 && st <= 1.0, msg: `Толщина среза ${st} мм > 0.75 мм (оптимум)`, severity: 'warning' },
          { check: () => params.kvp > 120, msg: `kVp = ${params.kvp} (обычно 100-120)`, severity: 'warning' },
          { check: () => params.heartRate && params.heartRate > 70, msg: `ЧСС ${params.heartRate} > 70 (риск артефактов)`, severity: 'warning' },
          { check: () => params.heartRate && params.heartRate > 90, msg: `ЧСС ${params.heartRate} > 90 (неприемлемо)`, severity: 'error' },
        ],
      },
      'calcium-score': {
        name: 'Calcium Score (Agatston)',
        rules: [
          { check: () => params.hasContrast, msg: 'Должно быть нативное (без контраста)', severity: 'error' },
          { check: () => st < 2.5 || st > 3.5, msg: `Толщина среза ${st} мм ≠ 3.0 (стандарт)`, severity: 'error' },
          { check: () => params.kvp && params.kvp !== 120, msg: `kVp = ${params.kvp} ≠ 120`, severity: 'warning' },
        ],
      },
      'pe-cta': {
        name: 'КТ-ангиография (ТЭЛА)',
        rules: [
          { check: () => !params.hasContrast, msg: 'Требуется контрастное усиление', severity: 'error' },
          { check: () => st > 2.0, msg: `Толщина среза ${st} мм > 2.0 мм`, severity: 'warning' },
          { check: () => st > 3.0, msg: `Толщина среза ${st} мм > 3.0 мм`, severity: 'error' },
        ],
      },
      'contrast-ct': {
        name: 'КТ с контрастом',
        rules: [
          { check: () => !params.hasContrast, msg: 'Контраст не обнаружен', severity: 'error' },
          { check: () => st > 5.0, msg: `Толщина среза ${st} мм > 5.0 мм`, severity: 'warning' },
        ],
      },
      'non-contrast-ct': {
        name: 'КТ без контраста',
        rules: [
          { check: () => st > 5.0, msg: `Толщина среза ${st} мм > 5.0 мм`, severity: 'warning' },
        ],
      },
    };

    const proto = PROTOCOLS[protocol];
    if (!proto) return { protocol, compliant: true, findings };

    for (const rule of proto.rules) {
      try { if (rule.check() && rule.msg) findings.push({ severity: rule.severity, message: rule.msg }); } catch (e) { log.debug("[studyQualityAnalyzer]", e.message); }
    }

    return { protocol, protocolName: proto.name, compliant: !findings.some(f => f.severity === 'error'), findings };
  }

  /**
   * [v8] Сравнение двух наборов параметров
   */
  static compareParams(params1, params2) {
    const keys = ['sliceThickness','kvp','mA','mAs','kernel','fov','pitch','pixelSpacingX','hasContrast','hasECGGating','heartRate','rotationTime','bitsStored','rows','columns'];
    const diff = {};
    for (const k of keys) {
      if (params1[k] !== params2[k]) diff[k] = { study1: params1[k], study2: params2[k] };
    }

    const recon1 = this.classifyRecon(params1, this.classifyScanner(params1));
    const recon2 = this.classifyRecon(params2, this.classifyScanner(params2));
    if (recon1.type !== recon2.type) diff.reconstruction = { study1: recon1.label, study2: recon2.label };

    const dose1 = this.calcDose(params1);
    const dose2 = this.calcDose(params2);
    if (dose1.effectiveDose !== dose2.effectiveDose) diff.dose = { study1: dose1.effectiveDose, study2: dose2.effectiveDose };

    const score1 = this.computeQualityScore(params1);
    const score2 = this.computeQualityScore(params2);

    return { identical: Object.keys(diff).length === 0, differences: diff, score1: score1.score, score2: score2.score, betterStudy: score1.score > score2.score ? 1 : score1.score < score2.score ? 2 : 0 };
  }

  /**
   * [v8] JSON-отчёт для внешних систем
   */
  static exportAnalysisJSON(analysis) {
    return JSON.stringify({
      version: 2, exportedAt: new Date().toISOString(),
      modality: analysis.params?.modality, isCT: analysis.isCT,
      score: analysis.isCT ? this.computeQualityScore(analysis.params) : null,
      kernel: analysis.kernel, phase: analysis.phase, scanner: analysis.scanner,
      dose: analysis.dose, temporal: analysis.temporal, detector: analysis.detector,
      recon: analysis.recon, imgType: analysis.imgType, noiseLevel: analysis.noiseLevel,
      capabilities: analysis.capabilities?.length || 0, limitations: analysis.limitations?.length || 0,
      recommendations: analysis.recommendations, summary: analysis.summary,
    }, null, 2);
  }

  /** [v8] Список поддерживаемых протоколов */
  static listProtocols() {
    return [
      { id: 'cardiac-cta', name: 'КТ-коронарография' },
      { id: 'calcium-score', name: 'Calcium Score (Agatston)' },
      { id: 'pe-cta', name: 'КТ-ангиография (ТЭЛА)' },
      { id: 'contrast-ct', name: 'КТ с контрастом' },
      { id: 'non-contrast-ct', name: 'КТ без контраста' },
      { id: 'auto', name: 'Авто-определение' },
    ];
  }

  /** [v8] Пригоден ли набор для конкретной клинической задачи */
  static isAdequateFor(params, task) {
    const st = params.sliceThickness ?? params.sliceSpacing;
    const checks = {
      'coronary-cta':  () => params.hasContrast && params.hasECGGating && st <= 1.0,
      'calcium-score': () => !params.hasContrast && st >= 2.5 && st <= 3.5,
      'pe-cta':        () => params.hasContrast && st <= 2.0,
      'aorta':         () => params.hasContrast && st <= 3.0,
      'hrct-lungs':    () => st <= 1.5,
      'vrt-3d':        () => st <= 2.0,
      'cpr':           () => params.hasContrast && st <= 1.0,
      'tavi-planning': () => params.hasContrast && params.hasECGGating && st <= 1.0,
      'mip':           () => st <= 2.0,
    };
    const fn = checks[task];
    if (!fn) return { task, adequate: false, error: `Unknown task: ${task}. Available: ${Object.keys(checks).join(', ')}` };
    return { task, adequate: fn(), params: { sliceThickness: st, hasContrast: params.hasContrast, hasECGGating: params.hasECGGating } };
  }

  /** [v8] Все доступные клинические задачи */
  static listClinicalTasks() {
    return ['coronary-cta','calcium-score','pe-cta','aorta','hrct-lungs','vrt-3d','cpr','tavi-planning','mip'];
  }
}